import { ENCODER_EMPTY_MARKER } from './utils';

function createStructureEncoder<TMarker extends string, TInstance>(
  marker: TMarker,
  config: {
    shouldUse: (instance: unknown) => boolean;
    serialize: (
      instance: TInstance,
      next: (key: string, value?: any) => void,
    ) => void;
    deserialize: {
      create: () => TInstance;
      has: (instance: TInstance, key: string) => boolean;
      append: (instance: TInstance, key: string, value: any) => void;
    };
  },
) {
  return {
    marker,
    ...config,
  };
}

export const arrayEncoder = createStructureEncoder<'A', any[]>('A', {
  shouldUse: (instance) => Array.isArray(instance),
  serialize: (instance, next) => {
    if (!instance.length) {
      next('', ENCODER_EMPTY_MARKER);
    } else {
      instance.forEach((value, index) => {
        next(`${index}`, value);
      });
    }
  },
  deserialize: {
    create: () => [],
    has: (instance, key) =>
      !(typeof instance[key as unknown as number] === 'undefined'),
    append: (instance, _key, value) => {
      instance.push(value);
    },
  },
});

export const mapEncoder = createStructureEncoder<'M', Map<any, any>>('M', {
  shouldUse: (instance: unknown) => instance instanceof Map,
  serialize: (instance, next) => {
    instance.forEach((value, key) => {
      next(key, value);
    });
  },
  deserialize: {
    create: () => new Map(),
    has: (instance, key) => instance.has(key),
    append: (instance, key, value) => {
      instance.set(key, value);
    },
  },
});

export const setEncoder = createStructureEncoder<'S', Set<any>>('S', {
  shouldUse: (instance) => instance instanceof Set,
  serialize: (instance, next) => {
    let index = 0;
    instance.forEach((value) => {
      next(`${index++}`, value);
    });
  },
  deserialize: {
    create: () => new Set(),
    has: (instance, key) => instance.has(key),
    append: (instance, _key, value) => {
      instance.add(value);
    },
  },
});

export const objectEncoder = createStructureEncoder<'O', Record<string, any>>(
  'O',
  {
    shouldUse: (instance) => typeof instance === 'object',
    serialize: (root, next) => {
      const forEachMember = (instance: Record<string, any>) => {
        const keys = Reflect.ownKeys(instance);

        const members = keys
          .filter((key) => {
            return key !== 'constructor';
          })
          .filter((key) => {
            return typeof key !== 'symbol';
          });

        if (!members.length) {
          next('', ENCODER_EMPTY_MARKER);
        } else {
          members
            .filter((key) => {
              return key !== 'constructor';
            })
            .filter((key) => {
              return typeof key !== 'symbol';
            })
            .forEach((key) => {
              const value = Reflect.get(root, key);

              next(key as string, value);
            });
        }

        const parent = Reflect.getPrototypeOf(instance);
        if (!parent || parent?.hasOwnProperty('__proto__')) {
          return;
        }

        forEachMember(parent);
      };

      forEachMember(root);
    },
    deserialize: {
      create: () => ({}),
      has: (instance, key) => !!instance[key],
      append: (instance, key, value) => {
        instance[key] = value;
      },
    },
  },
);
